
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';

export default function JobScoringSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Refs to control opening the native date picker on click anywhere in the input
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  // Helper: parse YYYY-MM-DD as a local date to avoid timezone shifts
  const parseLocalYMD = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };
  const formatDisplayDate = (ymd) => {
    const dt = parseLocalYMD(ymd);
    return dt
      ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
  };

  // Helper: open native date picker safely only on real user gestures
  const openPicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
      } catch (_) {
        // Ignore NotAllowedError when browser thinks it's not a user gesture
        el.focus();
      }
    } else {
      el.focus();
    }
  };

  // Set default date range to last 7 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6); // 7 days ago (including today)
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [pageSize, setPageSize] = useState(100);
  const [offset, setOffset] = useState(0);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const fetchSummary = (newOffset = offset) => {
    setLoading(true);
    let url = `/scoring-summary?offset=${newOffset}&pageSize=${pageSize}`;
    // Always use date range
    url += `&start_date=${startDate}&end_date=${endDate}`;
    
    axios.get(url, { headers: { Accept: 'application/json' } })
      .then(res => {
        setSummary(res.data);
        setLoading(false);
        setOffset(res.data.offset || 0);
        setPageSize(res.data.page_size || 100);
      })
      .catch(() => {
        setError('Failed to fetch job scoring summary');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSummary(0);
  }, [startDate, endDate, pageSize]);

  useEffect(() => {
    if (offset > 0) {
      fetchSummary(offset);
    }
  }, [offset]);

  useEffect(() => {
    if (summary && chartRef.current) {
      if (chartInstance.current) chartInstance.current.destroy();
      // Bucket labels for 0–100 scale
      const bucketLabels = ['<30', '30-60', '60-80', '≥80'];

      chartInstance.current = new Chart(chartRef.current, {
        type: 'bar',
        data: {
          labels: bucketLabels,
          datasets: [{
            label: 'Count',
            data: [
              summary.score_dist?.less_than_03 || 0,
              summary.score_dist?.between_03_06 || 0,
              summary.score_dist?.between_06_08 || 0,
              summary.score_dist?.over_08 || 0
            ],
            backgroundColor: [
              '#ef4444', '#f59e42', '#60a5fa', '#34d399'
            ],
            borderRadius: 8,
            barPercentage: 0.6,
            categoryPercentage: 0.7
          }]
        },
        options: {
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                // Ensure tooltip title matches our 0–100 bucket labels
                title: (items) => {
                  const idx = items?.[0]?.dataIndex ?? 0;
                  return bucketLabels[idx] || '';
                }
              }
            }
          },
          scales: {
            x: {
              ticks: {
                color: '#374151',
                font: { size: 16, weight: 'bold' },
                callback: (_, idx) => bucketLabels[idx] || ''
              }
            },
            y: { beginAtZero: true, ticks: { color: '#374151', font: { size: 16 } } }
          }
        }
      });
    }
  }, [summary]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <div className="w-full max-w-6xl mx-auto mt-8">
      <h1 className="text-4xl font-bold mb-6">Job Scoring Summary</h1>
      <div className="flex flex-col md:flex-row items-center justify-center gap-3 mb-6">
        <div className="flex gap-2 items-center justify-center">
          <input
            type="date"
            value={startDate}
            ref={startInputRef}
            onChange={(e) => { 
              setStartDate(e.target.value); 
              setOffset(0);
            }}
            onMouseDown={(e) => { e.preventDefault(); openPicker(startInputRef); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(startInputRef); } }}
            className="w-40 px-3 py-2 rounded border bg-white text-gray-700 cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            placeholder="Start Date"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            ref={endInputRef}
            onChange={(e) => { 
              setEndDate(e.target.value); 
              setOffset(0);
            }}
            onMouseDown={(e) => { e.preventDefault(); openPicker(endInputRef); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(endInputRef); } }}
            className="w-40 px-3 py-2 rounded border bg-white text-gray-700 cursor-pointer hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            placeholder="End Date"
          />
          <button
            title="Reset to last 7 days"
            onClick={() => { 
              const today = new Date();
              const weekAgo = new Date();
              weekAgo.setDate(today.getDate() - 6);
              setStartDate(`${weekAgo.getFullYear()}-${String(weekAgo.getMonth()+1).padStart(2,'0')}-${String(weekAgo.getDate()).padStart(2,'0')}`);
              setEndDate(`${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`);
              setOffset(0); 
            }}
            className="px-3 py-2 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >&#8635;</button>
        </div>
      </div>
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
        <span className="text-blue-700">
          Showing scoring data for jobs uploaded from {formatDisplayDate(startDate)} to {formatDisplayDate(endDate)}
        </span>
      </div>
      <div className="flex flex-row flex-wrap gap-6 mb-8 justify-center items-stretch">
        <StatCard label="Total Leads" value={summary?.total_leads} color="purple" />
        <StatCard label="Total Scores" value={summary?.total_scores} color="blue" />
  <StatCard label="High Scores (≥80)" value={summary?.high_scores} color="green" />
  <StatCard label="Avg. Jobs Scored per Lead" value={summary?.avg_scores_per_lead} color="violet" />
      </div>
      <div className="bg-white rounded-lg shadow p-6 mt-4">
        <h2 className="text-2xl font-bold text-center mb-4">Overall Score Distribution</h2>
        <canvas ref={chartRef} height="120"></canvas>
        <div className="flex justify-center mt-4">
          <div className="flex gap-6">
            <Legend color="red-400" label="<30" />
            <Legend color="yellow-400" label="30-60" />
            <Legend color="blue-400" label="60-80" />
            <Legend color="green-400" label="≥80" />
          </div>
        </div>
      </div>
      {/* Lead Scoring Details Table */}
      <div className="mt-16">
        <div className="bg-indigo-50 py-6 px-8 rounded-t-lg">
          <h2 className="text-3xl font-bold text-indigo-700">Lead Scoring Details</h2>
        </div>
        <div className="bg-white rounded-b-lg shadow overflow-x-auto">
          <table className="min-w-full text-left">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-6 text-xl font-bold">Lead Name</th>
                <th className="py-4 px-6 text-xl font-bold">Target Role</th>
                <th className="py-4 px-6 text-xl font-bold">Start Date</th>
                <th className="py-4 px-6 text-xl font-bold">Jobs Scored</th>
                <th className="py-4 px-6 text-xl font-bold">Score Distribution</th>
              </tr>
            </thead>
            <tbody>
              {summary?.results?.length ? summary.results.map(row => (
                <tr key={row.id} className="border-b bg-gray-50">
                  <td className="py-4 px-6 align-middle">
                    <div className="font-semibold text-lg text-black">{row.lead_name}</div>
                    <div className="text-gray-500 text-sm">ID: {row.id?.toString().slice(0,10)}...</div>
                  </td>
                  <td className="py-4 px-6 align-middle font-semibold text-lg">{row.target_role}</td>
                  <td className="py-4 px-6 align-middle font-semibold text-lg">{row.start_date ? formatDisplayDate(row.start_date) : ''}</td>
                  <td className="py-4 px-6 align-middle font-semibold text-lg">{row.jobs_scored}</td>
                  <td className="py-4 px-6 align-middle">
                    <ScoreBar dist={row.score_distribution} />
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="py-8 px-6 text-center text-gray-400 text-xl">No leads found for this date.</td></tr>
              )}
            </tbody>
          </table>
          <div className="flex justify-center items-center py-6">
            <button onClick={() => fetchSummary(summary?.prev_offset)} disabled={summary?.prev_offset == null} className="px-4 py-2 mx-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors">Previous</button>
            <span className="mx-2 text-lg font-medium">Page {Math.floor((summary?.offset || 0) / (summary?.page_size || 100)) + 1} of {Math.ceil((summary?.total_count || 0) / (summary?.page_size || 100))}</span>
            <button onClick={() => fetchSummary(summary?.next_offset)} disabled={summary?.next_offset == null} className="px-4 py-2 mx-2 rounded bg-gray-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colorMap = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    violet: 'bg-violet-500',
  };
  return (
    <div className="bg-white rounded-lg shadow p-6 flex flex-row items-center justify-center">
      <div className="flex-shrink-0 flex items-center justify-center mr-4">
        <span className={`inline-block ${colorMap[color]} rounded-lg p-2`}>
          {/* Icon placeholder, you can add SVGs here for each stat */}
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="white" className="w-8 h-8">
            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" fill="none" />
          </svg>
        </span>
      </div>
      <div className="flex flex-col items-start justify-center">
        <span className="text-gray-500 text-lg font-medium">{label}</span>
        <span className="text-3xl font-bold">{value}</span>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  const colorMap = {
    'red-400': 'bg-red-400',
    'yellow-400': 'bg-yellow-400',
    'blue-400': 'bg-blue-400',
    'green-400': 'bg-green-400',
  };
  return (
    <div className="flex items-center gap-2"><span className={`w-4 h-4 ${colorMap[color]} inline-block rounded`}></span> <span className="text-gray-700">{label}</span></div>
  );
}

function ScoreBar({ dist }) {
  if (!dist) return null;
  const total = (dist.less_than_03 || 0) + (dist.between_03_06 || 0) + (dist.between_06_08 || 0) + (dist.over_08 || 0);
  const colors = [
    { key: 'less_than_03', color: 'bg-red-400' },
    { key: 'between_03_06', color: 'bg-yellow-400' },
    { key: 'between_06_08', color: 'bg-blue-400' },
    { key: 'over_08', color: 'bg-green-400' }
  ];
  return (
    <div>
      <div className='flex justify-between text-xs font-semibold mt-2 mb-1'>
  <span className='text-red-500'>&lt;30</span>
  <span className='text-yellow-500'>30-60</span>
  <span className='text-blue-500'>60-80</span>
  <span className='text-green-500'>&ge;80</span>
      </div>
      <div className="flex h-6 w-full rounded overflow-hidden">
        {colors.map(({key, color}) => {
          const val = dist[key] || 0;
          const percent = total ? Math.round((val/total)*100) : 0;
          return percent > 0 ? <div key={key} className={`${color}`} style={{width: `${percent}%`}}></div> : null;
        })}
        {total === 0 && <div className="bg-gray-200 w-full"></div>}
      </div>
      <div className='flex justify-between text-xs text-black font-bold'>
        <span>{dist.less_than_03 || 0}</span>
        <span>{dist.between_03_06 || 0}</span>
        <span>{dist.between_06_08 || 0}</span>
        <span>{dist.over_08 || 0}</span>
      </div>
    </div>
  );
}
